import React from 'react';
import { motion } from 'motion/react';
import { ShoppingCart, Globe, Cloud, Building2, ShieldCheck, ArrowRight } from 'lucide-react';
import { INDUSTRIES } from '@/src/constants';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/src/components/SEO';
import { servicesSchema } from '@/src/lib/schema';

const industryIcons = [ShoppingCart, Globe, Cloud, Building2];

export default function Industries() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="Industries Served – Specialized Cybersecurity Expertise | AGRANDINTL"
        description="AGRANDINTL provides specialized cybersecurity consulting for retail, e-commerce, cloud-based businesses, and government contractors in the DMV area."
        keywords="retail cybersecurity, e-commerce security DC, cloud business protection, government contractor security virginia, industry-specific cybersecurity"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Industries Served",
          "description": "Specialized cybersecurity expertise for various industries.",
          "relatedService": servicesSchema
        }}
      />
      {/* Page Header */}
      <section className="py-20 bg-secondary/[0.01] relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em] mb-8"
            >
              Sector-Specific Expertise
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl lg:text-6xl font-display font-bold mb-8 tracking-tight leading-tight"
            >
              Industries <br />
              <span className="text-accent">We Serve</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed font-normal max-w-2xl"
            >
              We provide professional security solutions tailored to the specific needs of your business sector.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Industry Sections */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {INDUSTRIES.map((industry, index) => {
              const Icon = industryIcons[index];
              return (
                <motion.div
                  key={industry.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="glass-card h-full border-white/5 hover:border-accent/10 transition-all rounded-2xl overflow-hidden group">
                    <CardContent className="p-8 space-y-6 relative">
                      <div className="flex items-center gap-4 relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h2 className="text-xl md:text-2xl font-display font-bold tracking-tight">{industry.title}</h2>
                      </div>
                      
                      <p className="text-sm text-muted-foreground leading-relaxed font-normal relative z-10">
                        {industry.description}
                      </p>

                      <div className="space-y-3 relative z-10">
                        <h3 className="font-bold font-display text-accent uppercase tracking-[0.2em] text-[8px]">Key Challenges</h3>
                        <motion.ul 
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          variants={{
                            hidden: { opacity: 0 },
                            visible: {
                              opacity: 1,
                              transition: { staggerChildren: 0.1 }
                            }
                          }}
                          className="space-y-2"
                        >
                          {industry.challenges.map((challenge) => (
                            <motion.li 
                              key={challenge} 
                              variants={{
                                hidden: { opacity: 0, x: -5 },
                                visible: { opacity: 1, x: 0 }
                              }}
                              className="flex items-center gap-2.5 text-xs text-foreground/80 font-medium group/item"
                            >
                              <div className="w-1 h-1 rounded-full bg-accent group-hover/item:scale-125 transition-transform" />
                              {challenge}
                            </motion.li>
                          ))}
                        </motion.ul>
                      </div>

                      <div className="p-5 bg-accent/[0.01] rounded-xl border border-accent/10 relative z-10">
                        <h3 className="font-bold font-display text-[9px] mb-1.5 uppercase tracking-widest">Our Solution</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed font-normal">
                          {industry.solution}
                        </p>
                      </div>

                      <Button asChild variant="link" className="px-0 text-accent font-bold group text-sm hover:no-underline relative z-10 h-auto p-0">
                        <Link to="/contact" className="flex items-center gap-2">
                          Get Industry-Specific Quote <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Industry Expertise Matters */}
      <section className="py-24 bg-secondary/[0.01] relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[8px] font-bold uppercase tracking-[0.3em]">
              Strategic Differentiation
            </div>
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">Why Industry <br /> <span className="text-accent">Expertise Matters</span></h2>
            <p className="text-base text-muted-foreground leading-relaxed font-normal max-w-2xl mx-auto">
              A "one-size-fits-all" approach to cybersecurity often leaves critical gaps. We understand the nuances of different sectors and build defenses that address your specific regulatory and operational risks.
            </p>
            <div className="pt-4">
              <Button asChild size="lg" className="font-bold px-8 h-12 rounded-xl transition-all duration-300 text-sm">
                <Link to="/contact">Discuss Your Industry Needs</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
